// liveTheme.js — Comprehend Live surface tokens.
// Darker than the app, but unmistakably Comprehend: surfaces are deep
// spruce derived from the brand greens ($green #2dce89 / gradient
// #2dce89→#2dcecc), accents are the Argon semantic palette — not a
// generic dark-HUD navy.
export const T = {
  surface: '#13302a',
  surfaceCard: '#1d453a',
  surfaceMuted: '#17382f',
  surfaceDeep: '#0d231e',
  border: 'rgba(159,232,200,0.18)',
  textPrimary: '#f6fbf9',
  textSecondary: '#a9cabd',
  textSoft: '#d7eae1',
  textFaint: '#7da294',
  green: '#2dce89',
  greenBright: '#6bd888',
  greenDark: '#0d2b1a',
  gradient: 'linear-gradient(87deg, #2dce89 0, #2dcecc 100%)',
  red: '#f5365c',
  redSoft: '#fda4af',
  amber: '#fbbf24',
  teal: '#11cdef',
  tealSoft: '#7ce8f9',
};

export const severityColor = (severity) =>
  ({ urgent: T.red, warn: T.amber, info: T.teal })[severity] || T.teal;

/**
 * Agent mode indicator (the Synapse convention: green = live/armed,
 * BIG green = actively listening). Single source for rail, header, mic.
 */
export const agentMode = (state) => {
  if (state.agentStatus === 'listening')
    return {
      key: 'listening',
      label: 'LISTENING',
      color: T.greenBright,
      pulse: true,
      big: true,
    };
  if (state.agentSpeaking)
    return {
      key: 'speaking',
      label: 'SPEAKING',
      color: T.tealSoft,
      pulse: true,
    };
  if (state.agentStatus === 'connecting')
    return { key: 'connecting', label: 'WAKING', color: T.amber, pulse: true };
  if (state.agentStatus === 'error')
    return { key: 'error', label: 'OFFLINE', color: T.redSoft, pulse: false };
  if (state.wakeArmed)
    return { key: 'live', label: 'LIVE', color: T.greenBright, pulse: true };
  if (state.agentStatus === 'ready')
    return { key: 'ptt', label: 'PTT', color: T.green, pulse: false };
  return { key: 'standby', label: 'STANDBY', color: T.textFaint, pulse: false };
};

export const card = (accent) => ({
  background: T.surfaceCard,
  borderRadius: 8,
  borderLeft: accent ? `3px solid ${accent}` : undefined,
  padding: '8px 10px',
  marginBottom: 8,
});

export const pill = (bg, color) => ({
  background: bg,
  color,
  fontSize: 11,
  fontWeight: 600,
  padding: '2px 9px',
  borderRadius: 10,
  whiteSpace: 'nowrap',
});

export const buttonPrimary = {
  background: T.green,
  color: T.greenDark,
  fontSize: 12,
  fontWeight: 600,
  padding: '4px 12px',
  borderRadius: 6,
  border: 'none',
  cursor: 'pointer',
};

export const buttonGhost = {
  background: 'transparent',
  color: T.textSoft,
  fontSize: 12,
  fontWeight: 600,
  padding: '4px 12px',
  borderRadius: 6,
  border: `1px solid rgba(159,232,200,0.35)`,
  cursor: 'pointer',
};

export const LIVE_KEYFRAMES = `
/* App-wide low-profile scrollbars in the brand green (the chunky native
   bar clashed with the theme and its appearance/disappearance was
   shifting layout). Panel scrollers (.cpl-scroll/.cpl-rail) override. */
* { scrollbar-width: thin; scrollbar-color: rgba(45,206,137,.4) transparent; }
*::-webkit-scrollbar { width: 8px; height: 8px; }
*::-webkit-scrollbar-track { background: transparent; }
*::-webkit-scrollbar-thumb { background: rgba(45,206,137,.35); border-radius: 4px; }
*::-webkit-scrollbar-thumb:hover { background: rgba(45,206,137,.6); }
@keyframes cplPulse { 0%,100%{opacity:1} 50%{opacity:.3} }
@keyframes cplWork { 0%{transform:translateX(-100%)} 100%{transform:translateX(300%)} }
@keyframes cplSlide { 0%{transform:translateY(-8px);opacity:0} 100%{transform:translateY(0);opacity:1} }
@keyframes cplGlow { 0%,100%{box-shadow:0 0 0 0 rgba(107,216,136,.55)} 50%{box-shadow:0 0 0 9px rgba(107,216,136,0)} }
@keyframes cplRise { 0%{transform:translateY(14px);opacity:0} 100%{transform:translateY(0);opacity:1} }
@keyframes cplCardOut { 0%{opacity:1; transform:translateX(0); max-height:180px;} 55%{opacity:0; transform:translateX(26px); max-height:180px;} 100%{opacity:0; transform:translateX(26px); max-height:0; margin-bottom:0;} }
.cpl-card-out { animation: cplCardOut .65s ease forwards; overflow:hidden; pointer-events:none; }
@keyframes cplFlash { 0%,100%{box-shadow:0 0 0 2px rgba(107,216,136,.5)} 45%{box-shadow:0 0 0 5px rgba(107,216,136,.25)} }
.cpl-flash { animation: cplFlash .9s ease-in-out infinite; border-radius: 8px; }
@keyframes cplFadeIn { 0%{opacity:0} 100%{opacity:1} }
@property --cpl-angle { syntax: '<angle>'; initial-value: 0deg; inherits: false; }
@keyframes cplOrbit { to { --cpl-angle: 360deg; } }
.cpl-orbit, .cpl-orbit-sm { position: relative; }
.cpl-orbit::before, .cpl-orbit-sm::before {
  content: ''; position: absolute; inset: -2px; border-radius: 10px; padding: 2px;
  background: conic-gradient(from var(--cpl-angle),
    transparent 0deg, transparent 270deg,
    rgba(107,216,136,.15) 290deg, rgba(107,216,136,.7) 330deg, #6bd888 358deg, transparent 360deg);
  -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
  -webkit-mask-composite: xor; mask-composite: exclude;
  animation: cplOrbit 1.1s linear infinite; pointer-events: none; z-index: 2;
}
.cpl-orbit-sm::before { inset: -2px; border-radius: 8px; }
.cpl-dot { display:inline-block; width:8px; height:8px; border-radius:50%; animation:cplPulse 1.6s ease-in-out infinite; }
.cpl-glow { animation: cplGlow 1.2s ease-in-out infinite; }
@keyframes cplTyping { 0%,60%,100%{opacity:.25; transform:translateY(0)} 30%{opacity:1; transform:translateY(-3px)} }
.cpl-typing span { display:inline-block; width:5px; height:5px; border-radius:50%; background:#a9cabd; animation:cplTyping 1.2s ease-in-out infinite; }
.cpl-typing span:nth-child(2) { animation-delay:.15s; }
.cpl-typing span:nth-child(3) { animation-delay:.3s; }
.cpl-scroll { scrollbar-width: thin; scrollbar-color: rgba(159,232,200,.35) transparent; overscroll-behavior: contain; }
.cpl-scroll::-webkit-scrollbar { width: 6px; height: 6px; }
.cpl-scroll::-webkit-scrollbar-track { background: transparent; }
.cpl-scroll::-webkit-scrollbar-thumb { background: rgba(159,232,200,.25); border-radius: 3px; }
.cpl-scroll::-webkit-scrollbar-thumb:hover { background: rgba(159,232,200,.5); }
.cpl-rail { scrollbar-width: none; overscroll-behavior: contain; }
.cpl-rail::-webkit-scrollbar { display: none; height: 0; }
/* Recording wave — equalizer bars that bounce while recording; their height
   is also scaled live by mic level (--cpl-wave, 0..1) so it reads as
   "hearing you", not just a generic spinner. */
@keyframes cplWave { 0%,100%{transform:scaleY(0.35)} 50%{transform:scaleY(1)} }
.cpl-wave { display:inline-flex; align-items:center; gap:2px; height:14px; }
.cpl-wave span {
  width:2.5px; height:100%; border-radius:2px; background:#f5365c;
  transform-origin:center;
  transform:scaleY(calc(0.3 + 0.7 * var(--cpl-wave, 0.2)));
  animation: cplWave 0.9s ease-in-out infinite;
}
.cpl-wave span:nth-child(2){ animation-delay:.15s }
.cpl-wave span:nth-child(3){ animation-delay:.3s }
.cpl-wave span:nth-child(4){ animation-delay:.45s }
.cpl-wave span:nth-child(5){ animation-delay:.6s }

/* Siri-style listening aura: a blue-green light traveling around the
   whole viewport edge (same conic + mask trick as the card orbit ring,
   full-screen) over a breathing inner glow. pointer-events:none so it
   never blocks the UI underneath. */
@property --aura-angle { syntax: '<angle>'; initial-value: 0deg; inherits: false; }
@keyframes cplAuraSpin { to { --aura-angle: 360deg; } }
@keyframes cplAuraBreath { 0%,100%{opacity:.55} 50%{opacity:1} }
@keyframes cplAuraIn { from{opacity:0} to{opacity:1} }
@keyframes cplAuraOut { from{opacity:1} to{opacity:0} }
.cpl-aura { position: fixed; inset: 0; pointer-events: none; z-index: 10000002; }
/* Distinct names so flipping .in -> .out actually RESTARTS the animation;
   reusing one name leaves it parked at its end state (abrupt cut-off). */
.cpl-aura.in { animation: cplAuraIn 1s ease-out forwards; }
.cpl-aura.out { animation: cplAuraOut 1s ease-in forwards; }
.cpl-aura-glow {
  position: absolute; inset: 0; pointer-events: none;
  box-shadow: inset 0 0 70px 6px rgba(17,205,239,.22),
              inset 0 0 150px 26px rgba(45,206,137,.16);
  animation: cplAuraBreath 2.4s ease-in-out infinite;
}
.cpl-aura-ring, .cpl-aura-bloom {
  position: absolute; inset: 0; pointer-events: none;
  background: conic-gradient(from var(--aura-angle),
    rgba(17,205,239,0) 0deg, #11cdef 55deg, #2dce89 110deg,
    rgba(45,206,137,0) 165deg, rgba(17,205,239,0) 195deg,
    #6bd888 250deg, #11cdef 305deg, rgba(17,205,239,0) 360deg);
  -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
  -webkit-mask-composite: xor; mask-composite: exclude;
  animation: cplAuraSpin 4s linear infinite;
}
.cpl-aura-ring { padding: 4px; }
.cpl-aura-bloom { padding: 9px; filter: blur(14px); opacity: .8; }
`;
